/** Pure presentation helpers for the dashboard (nl-NL, EUR). All money arrives as EUR minor units. */
import { PERIOD_DAYS, type PeriodKey, type VatMode } from "@apphq/shared/metrics";
import type { LinkStatus, MetricPoint, OverviewEventType, SubscriptionStatus, TransactionKind } from "@apphq/shared/types";

export const VAT_LABELS: Readonly<Record<VatMode, string>> = {
  gross: "Bruto",
  proceeds: "Netto",
};

export const VAT_SUBTITLES: Readonly<Record<VatMode, string>> = {
  gross: "bruto",
  proceeds: "netto na kosten",
};

export const PERIOD_LABELS: Readonly<Record<PeriodKey, string>> = {
  "7d": "7D",
  "30d": "30D",
  "90d": "90D",
  "12m": "12M",
};

export const PERIOD_LONG: Readonly<Record<PeriodKey, string>> = {
  "7d": "laatste 7 dagen",
  "30d": "laatste 30 dagen",
  "90d": "laatste 90 dagen",
  "12m": "laatste 12 maanden",
};

const eurFmt = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const eurWholeFmt = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });
const intFmt = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 1 });

const NBSP = /[  ]/g;
/** Intl pads with no-break spaces; plain spaces wrap predictably and compare easily. */
function plain(s: string): string {
  return s.replace(NBSP, " ");
}

/** `eur(123456)` -> `€ 1.234,56`. null/NaN -> `—` (never a silent 0). */
export function eur(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return "—";
  return plain(eurFmt.format(minor / 100));
}

/** Axis ticks: whole euros, `€ 1,2k` from a thousand up. */
export function eurCompact(minor: number): string {
  const euros = minor / 100;
  const abs = Math.abs(euros);
  if (abs >= 1000) return plain(`€ ${pctFmt.format(euros / 1000)}k`);
  return plain(eurWholeFmt.format(euros));
}

export function int(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return plain(intFmt.format(n));
}

/** Ratio (0.125) -> `12,5%`. null -> `—`. */
export function pct(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "—";
  return `${plain(pctFmt.format(ratio * 100))}%`;
}

export type DeltaTone = "up" | "down" | "flat" | "none";
export interface Delta {
  /** Relative change as a ratio (0.1 = +10%); null when not comparable. */
  ratio: number | null;
  tone: DeltaTone;
  text: string;
}

/** Delta chip data. A missing or zero baseline is not comparable (no division by zero, no fake +∞%). */
export function delta(value: number, previous: number | null | undefined): Delta {
  if (previous === null || previous === undefined || !Number.isFinite(previous) || previous === 0) {
    return { ratio: null, tone: "none", text: "—" };
  }
  const ratio = (value - previous) / Math.abs(previous);
  if (Math.abs(ratio) < 0.0005) return { ratio: 0, tone: "flat", text: "0%" };
  const arrow = ratio > 0 ? "↑" : "↓";
  return { ratio, tone: ratio > 0 ? "up" : "down", text: `${arrow} ${pct(Math.abs(ratio))}` };
}

/** Picks the amount for the active VAT mode from a per-mode record (no refetch when switching). */
export function pickMode(byMode: Record<VatMode, number>, vat: VatMode): number {
  return byMode[vat];
}

const MONTHS_SHORT = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

/** `2026-10-23` -> `23 okt` (calendar day string, no timezone maths). */
export function dayShort(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  const month = MONTHS_SHORT[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : day;
}

export function dayLong(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  const month = MONTHS_SHORT[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : day;
}

const dateTimeFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: "Europe/Amsterdam",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});
const dateFmt = new Intl.DateTimeFormat("nl-NL", { timeZone: "Europe/Amsterdam", day: "numeric", month: "short", year: "numeric" });

export function dateTimeNl(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : plain(dateTimeFmt.format(d));
}

export function dateNl(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : plain(dateFmt.format(d));
}

/** `5 minuten geleden`-style label; falls back to a date after two days. */
export function relativeNl(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "nog nooit";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.round((now.getTime() - t) / 1000);
  if (sec < 45) return "zojuist";
  if (sec < 3600) return `${Math.round(sec / 60)} min geleden`;
  if (sec < 86400) return `${Math.round(sec / 3600)} uur geleden`;
  if (sec < 2 * 86400) return "gisteren";
  return dateNl(iso);
}

export interface ChartPoint {
  date: string;
  label: string;
  value: number;
}

/** Time series -> chart rows: sorted by day, de-duplicated (last wins), with an `d mmm` label. */
export function toChartPoints(points: readonly MetricPoint[]): ChartPoint[] {
  const byDay = new Map<string, number>();
  for (const p of points) byDay.set(p.date, p.value);
  return [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, value]) => ({ date, label: dayShort(date), value }));
}

/** Days covered by a period key (for subtitles / empty-state copy). */
export function periodDays(period: PeriodKey): number {
  return PERIOD_DAYS[period];
}

/** Width in percent (0-100) of a horizontal bar relative to the largest value; never NaN. */
export function barPercent(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0 || value <= 0) return 0;
  return Math.min(100, (value / max) * 100);
}

export interface Tone {
  label: string;
  tone: "good" | "warn" | "bad" | "neutral";
}

export function linkStatusMeta(status: LinkStatus): Tone {
  switch (status) {
    case "ok":
      return { label: "Gekoppeld", tone: "good" };
    case "linked_no_data":
      return { label: "Gekoppeld, nog geen data", tone: "warn" };
    case "problem":
      return { label: "Probleem", tone: "bad" };
  }
}

export function eventTypeMeta(type: OverviewEventType): Tone {
  switch (type) {
    case "new_subscription":
      return { label: "Nieuw abonnement", tone: "good" };
    case "renewal":
      return { label: "Verlenging", tone: "good" };
    case "lifetime":
      return { label: "Lifetime", tone: "good" };
    case "commission":
      return { label: "Commissie", tone: "good" };
    case "trial_started":
      return { label: "Trial", tone: "neutral" };
    case "refund":
      return { label: "Refund", tone: "bad" };
    case "system":
      return { label: "Systeem", tone: "neutral" };
  }
}

export function statusMeta(status: SubscriptionStatus | "none"): Tone {
  switch (status) {
    case "active":
      return { label: "Actief", tone: "good" };
    case "trial":
      return { label: "Trial", tone: "neutral" };
    case "grace":
      return { label: "Respijt", tone: "warn" };
    case "billing_retry":
      return { label: "Betaalprobleem", tone: "warn" };
    case "expired":
      return { label: "Verlopen", tone: "neutral" };
    case "refunded":
      return { label: "Terugbetaald", tone: "bad" };
    case "revoked":
      return { label: "Ingetrokken", tone: "bad" };
    case "none":
      return { label: "Geen abonnement", tone: "neutral" };
  }
}

export function kindMeta(kind: TransactionKind): Tone {
  switch (kind) {
    case "initial":
      return { label: "Nieuw", tone: "good" };
    case "renewal":
      return { label: "Verlenging", tone: "good" };
    case "one_time":
      return { label: "Eenmalig", tone: "neutral" };
    case "refund":
      return { label: "Refund", tone: "bad" };
    case "commission":
      return { label: "Commissie", tone: "good" };
  }
}

/** Human period: P1M -> `maandelijks`; unknown ISO shapes are shown as-is, null as `—`. */
export function periodNl(period: string | null | undefined): string {
  if (!period) return "—";
  switch (period.toUpperCase()) {
    case "P1W":
      return "wekelijks";
    case "P1M":
      return "maandelijks";
    case "P2M":
      return "2 maanden";
    case "P3M":
      return "3 maanden";
    case "P6M":
      return "6 maanden";
    case "P1Y":
      return "jaarlijks";
    default:
      return period;
  }
}

/** Country code (ISO-3166 alpha-2) -> Dutch name; unknown codes and `unknown` fall back gracefully. */
export function countryNl(code: string | null | undefined): string {
  if (!code || code.toLowerCase() === "unknown") return "Onbekend";
  try {
    return new Intl.DisplayNames(["nl-NL"], { type: "region" }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** Only http(s) links are rendered as a command-center button; anything else counts as "not set". */
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch {
    return null;
  }
}

/** Icon URL to render, or null (= show initials) when unsafe, missing, or it already failed to load. */
export function iconSrc(url: string | null | undefined, failed: string | null): string | null {
  // our own icons are served same-origin (/api/public/icons/…): allow a root-relative path, but not "//host"
  const src = url && /^\/(?!\/)/.test(url) ? url : safeHttpUrl(url);
  return src && src !== failed ? src : null;
}

/** Deterministic hue (0-359) for placeholder app icons. */
export function hueFor(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % 360;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts.length > 1 ? parts[1]?.[0] ?? "" : "";
  return (first + second).toUpperCase();
}
